import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { EFilterType, IFilterComponentProps } from './filter.types';
import { Box, Button, Divider, FormControl, InputAdornment, Menu, TextField } from '@mui/material';
import { styled, useTheme } from '@mui/material';
import moment from 'moment';
import { DateRange, RangeKeyDict } from 'react-date-range';
import Icon from 'DesignSystem/Primitives/Icon/Icon';

const DATE_FORMAT = 'YYYY-MM-DD';

const StyledCalendar = styled(DateRange)(({ theme }) => ({
  '& .rdrDay:not(.rdrDayPassive) .rdrInRange ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrStartEdge ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrEndEdge ~ .rdrDayNumber span, .rdrDay:not(.rdrDayPassive) .rdrSelected ~ .rdrDayNumber span':
    {
      color: `#000 !important`,
    },
  '& .rdrMonthAndYearWrapper': {
    padding: 0,
  },
  '& .rdrDayNumber span:after': {
    background: theme.custom.themeColors.accent[60],
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    height: '32px !Important',
    fontSize: 12,
  },

  '& .MuiInputBase-input': {
    fontSize: 12,
  },

  '& .MuiInputLabel-root': {
    top: 4,
    fontSize: 12,
  },
}));

const CalendarContainer = styled(Box)({
  display: 'flex',
});

const YearButtonsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  width: 100,
});

const YearButton = styled(Button)(({ theme }) => ({
  minWidth: 'unset',
  padding: '4px 8px',
  fontSize: 12,
  color: theme.custom.themeColors.black,

  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

const TimeFrameFilter: FC<IFilterComponentProps<EFilterType.TIME_FRAME>> = ({
  value,
  label = 'Time frame',
  displayPrevYears,
  onChange,
}) => {
  const anchorEl = useRef<HTMLDivElement>(null);
  const { custom } = useTheme();
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [internalRange, setInternalRange] = useState({
    startDate: value && value.startDate ? new Date(value.startDate) : undefined,
    endDate: value && value.endDate ? new Date(value.endDate) : new Date(''),
  });

  // Generate previous 7 years from current year
  const currentYear = new Date().getFullYear();
  const previousYears = Array.from({ length: 7 }, (_, i) => currentYear - i);

  // Sync internal state with value prop when opening picker
  useEffect(() => {
    if (openDatePicker) {
      setInternalRange({
        startDate: value && value.startDate ? new Date(value.startDate) : undefined,
        endDate: value && value.endDate ? new Date(value.endDate) : new Date(''),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDatePicker]);

  const selectionRange = {
    ...internalRange,
    color: custom.themeColors.accent[100],
    key: 'selection',
  };

  const formattedDateValue = useMemo(
    () =>
      value && value.startDate && value.endDate
        ? `${moment(value.startDate).format(DATE_FORMAT)} - ${moment(value.endDate).format(
            DATE_FORMAT
          )}`
        : '',
    [value]
  );

  const handleChange = (ranges: RangeKeyDict) => {
    const { startDate, endDate } = ranges.selection;
    setInternalRange({
      startDate: startDate ? startDate : undefined,
      endDate: endDate ? endDate : new Date(''),
    });
  };

  const handleTextFieldClick = () => {
    setOpenDatePicker(true);
  };

  const handleClose = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setOpenDatePicker(false);
    // Sync internal state to parent on close
    if (internalRange.startDate && internalRange.endDate) {
      onChange({
        startDate: internalRange.startDate,
        endDate: internalRange.endDate,
      });
    }
  };

  const handleYearSelect = (year: number) => {
    const startDate = new Date(year, 0, 1); // January 1st of selected year
    const endDate = new Date(year, 11, 31); // December 31st of selected year

    setInternalRange({
      startDate,
      endDate,
    });

    onChange({
      startDate,
      endDate,
    });

    setOpenDatePicker(false);
  };

  return (
    <FormControl fullWidth>
      <StyledTextField
        ref={anchorEl}
        value={formattedDateValue}
        label={label}
        placeholder="All results (Full history)"
        fullWidth
        size="small"
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Icon name="calendar" />
            </InputAdornment>
          ),
        }}
        onClick={handleTextFieldClick}
      />

      <Menu
        anchorEl={anchorEl.current}
        open={openDatePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={handleClose}
        disableAutoFocusItem
        disableEnforceFocus
      >
        <CalendarContainer>
          {displayPrevYears ? (
            <YearButtonsContainer>
              {previousYears.map(year => (
                <>
                  <YearButton
                    key={year}
                    variant="text"
                    onClick={() => handleYearSelect(year)}
                    data-testid={`year-button-${year}`}
                  >
                    {year}
                  </YearButton>
                  <Divider />
                </>
              ))}
            </YearButtonsContainer>
          ) : undefined}
          <StyledCalendar
            fixedHeight
            showDateDisplay={false}
            showMonthAndYearPickers={false}
            rangeColors={[custom.themeColors.accent[60]]}
            color={custom.themeColors.accent[60]}
            moveRangeOnFirstSelection={false}
            ranges={[selectionRange]}
            scroll={{ enabled: true }}
            maxDate={moment().add(2, 'week').toDate()}
            onChange={handleChange}
          />
        </CalendarContainer>
      </Menu>
    </FormControl>
  );
};

export default TimeFrameFilter;
